import {
  ChartNoAxesCombined,
  FileText,
  Search,
  Table2,
  UsersRound,
  type LucideIcon
} from "lucide-react";
import { useSceneScale } from "@/landing/use-scene-scale.ts";

const BASE = import.meta.env.BASE_URL;
const SCENE_W = 1504;
const SCENE_H = 1046;

const COST_SIGNALS = [
  { id: "price-up", label: "API 单价", trend: "↑" },
  { id: "token-limit", label: "套餐额度", trend: "↓" },
  { id: "token-overpay", label: "超额按量付费", trend: null }
] as const;

const CAPABILITIES: ReadonlyArray<{ label: string; Icon: LucideIcon }> = [
  { label: "知识检索", Icon: Search },
  { label: "文档处理", Icon: FileText },
  { label: "会议协作", Icon: UsersRound },
  { label: "表格分析", Icon: Table2 },
  { label: "业务分析", Icon: ChartNoAxesCombined }
];

const ORANGE_BRANCHES = [
  "M 1110 388 C 1184 388 1190 246 1244 246 S 1294 212 1344 212 H 1390",
  "M 1110 388 C 1192 388 1208 306 1260 306 S 1308 248 1360 248 H 1442",
  "M 1110 388 C 1182 388 1210 344 1262 344 S 1312 284 1366 284 H 1462",
  "M 1110 388 C 1200 388 1216 376 1272 376 S 1322 324 1378 324 H 1460",
  "M 1110 388 C 1200 388 1222 408 1282 408 S 1334 360 1392 360 H 1466",
  "M 1110 388 C 1194 388 1218 438 1270 438 S 1324 402 1384 402 H 1452",
  "M 1110 388 C 1188 388 1206 466 1260 466 S 1310 442 1362 442 H 1436",
  "M 1110 388 C 1178 388 1196 494 1246 494 S 1290 478 1342 478 H 1402"
] as const;

function plate(name: string) {
  return `${BASE}now/${name}.png`;
}

type NowCopyProps = {
  tone: "cost" | "capability";
  title: string;
  body: string;
};

function NowCopy({ tone, title, body }: NowCopyProps) {
  return (
    <div className="now-copy" data-tone={tone}>
      <div className="now-copy-title">
        <span className="now-mark" aria-hidden />
        <strong>{title}</strong>
      </div>
      <p>{body}</p>
    </div>
  );
}

function CostSignals() {
  return (
    <div className="now-cost-signals">
      {COST_SIGNALS.map((signal) => (
        <figure className="now-asset now-cost-signal" data-id={signal.id} key={signal.id}>
          <figcaption>
            {signal.label}
            {signal.trend ? <span aria-hidden>{signal.trend}</span> : null}
          </figcaption>
          <img src={plate(signal.id)} alt="" />
        </figure>
      ))}
    </div>
  );
}

function CapabilityRail() {
  return (
    <div className="now-capability-rail">
      {CAPABILITIES.map(({ label, Icon }) => (
        <div className="now-capability" key={label}>
          <Icon aria-hidden size={25} strokeWidth={1.45} />
          <span>{label}</span>
          <i aria-hidden />
        </div>
      ))}
    </div>
  );
}

function NowNetwork() {
  return (
    <svg className="now-network" viewBox={`0 0 ${SCENE_W} ${SCENE_H}`} aria-hidden>
      <defs>
        <filter id="now-blue-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="now-orange-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="now-blue-stroke" x1="0" x2="1">
          <stop stopColor="#2d7dff" />
          <stop offset="1" stopColor="#314cff" />
        </linearGradient>
      </defs>

      <g className="now-grid-lines">
        {Array.from({ length: 24 }, (_, index) => (
          <path key={`v-${index}`} d={`M ${52 + index * 60} 150 V 870`} />
        ))}
        {Array.from({ length: 13 }, (_, index) => (
          <path key={`h-${index}`} d={`M 32 ${170 + index * 56} H 1472`} />
        ))}
      </g>

      <g className="now-cloud-feed" filter="url(#now-blue-glow)">
        <path className="now-cloud-feed-halo" d="M 320 388 H 610 C 680 388 704 410 725 478 C 744 540 777 576 858 576 H 1018" />
        <path className="now-cloud-feed-core" d="M 320 388 H 610 C 680 388 704 410 725 478 C 744 540 777 576 858 576 H 1018" />
        <path className="now-cloud-feed-pulse" d="M 320 388 H 610 C 680 388 704 410 725 478 C 744 540 777 576 858 576 H 1018" />
        <path className="now-cloud-feed-fine" d="M 320 396 H 610 C 674 396 696 416 716 482 C 736 548 774 590 858 590 H 1012" />
      </g>

      <g className="now-workstation-rails" filter="url(#now-blue-glow)">
        <path className="now-workstation-rail" d="M 1018 576 H 1170" />
        <path className="now-workstation-rail" d="M 1012 590 H 1170" />
        <path className="now-workstation-distributor" d="M 1170 542 V 794" />
        <path className="now-workstation-rail-pulse" d="M 1018 576 H 1170" />
        <path className="now-workstation-rail-pulse" d="M 1012 590 H 1170" />
        <path className="now-workstation-distributor-pulse" d="M 1170 542 V 794" />
        {[542, 605, 668, 731, 794].map((y) => (
          <path className="now-workstation-branch" d={`M 1170 ${y} H 1180`} key={y} />
        ))}
      </g>

      <g className="now-model-links">
        <path d="M 618 762 V 782 H 936 V 762" />
        <path d="M 684 782 V 771" />
        <path d="M 784 782 V 771" />
        <path d="M 936 782 V 746" />
      </g>

      <g className="now-orange-feed" filter="url(#now-orange-glow)">
        <path className="now-orange-feed-main" d="M 320 388 H 1110" />
        <path className="now-orange-feed-pulse" d="M 320 388 H 1110" />
        {ORANGE_BRANCHES.map((path, index) => (
          <path className="now-orange-branch" d={path} key={index} />
        ))}
        <rect x="1274" y="301" width="9" height="9" />
        <rect x="1336" y="207" width="8" height="8" />
        <rect x="1374" y="355" width="9" height="9" />
        <rect x="1438" y="279" width="10" height="10" />
        <rect x="1455" y="437" width="8" height="8" />
      </g>

      <g className="now-junction" transform="translate(722 476)">
        <circle className="now-junction-ring" r="21" />
        <circle className="now-junction-ring" r="14" />
        <circle r="5" />
        <path d="M 0 -35 V -24 M 0 24 V 35 M -35 0 H -24 M 24 0 H 35" />
      </g>
    </svg>
  );
}

function NowScene() {
  const { frameRef, scale } = useSceneScale(SCENE_W);

  return (
    <div className="now-scene-frame" ref={frameRef} style={{ height: SCENE_H * scale }}>
      <div className="now-scene" style={{ transform: `scale(${scale})` }}>
        <NowNetwork />

        <header className="why-now-head">
          <h2>企业 AI 的成本与能力，终于在今天交叉。</h2>
          <p>
            当云端服务围绕调用量、套餐额度和附加 Credits 持续计费，
            <br />
            本地模型却快速跨过“够用”门槛。企业智能化第一次出现更可控的长期路径。
          </p>
        </header>

        <div className="now-cost-copy">
          <NowCopy
            tone="cost"
            title="使用越深入，云端账单越接近基础设施成本"
            body="更多用户、更长流程、更高调用频率，都会转化为持续增长的算力与订阅支出。"
          />
        </div>

        <figure className="now-asset now-cloud" data-id="cloud">
          <img src={plate("cloud")} alt="" />
          <figcaption>
            <strong>企业 AI 工作负载</strong>
            <span>（请求 / 文档 / 数据 / 流程）</span>
          </figcaption>
        </figure>

        <CostSignals />

        <p className="now-equation">足够强的模型 × 可控的成本 × 企业自己的数据边界</p>

        <div className="now-capability-copy">
          <NowCopy
            tone="capability"
            title="27B 级模型，正在把旗舰能力带回本地"
            body="Qwen 27B 级模型与 DeepSeek Flash 等高效架构，已经可以覆盖知识检索、文档、会议与数据分析等高频工作，并在企业可控的单机硬件上运行。"
          />
        </div>

        <figure className="now-asset now-dense-model" data-id="dense-model">
          <img src={plate("dense-model")} alt="" />
          <figcaption>Qwen · 27B 级</figcaption>
        </figure>

        <figure className="now-asset now-deepseek" data-id="deepseek-flash">
          <img src={plate("deepseek-flash")} alt="" />
          <figcaption>DeepSeek Flash</figcaption>
        </figure>

        <figure className="now-asset now-workstation" data-id="workstation">
          <img src={plate("workstation")} alt="" />
          <figcaption>单机<br />工作站</figcaption>
        </figure>

        <p className="now-model-bracket-label">逼近上代旗舰能力</p>

        <CapabilityRail />

        <p className="why-now-close">本地 AI 不再是妥协方案，而是企业智能化的新默认。</p>
        <div className="now-next" aria-hidden><span /></div>
      </div>
    </div>
  );
}

function WhyNowMobile() {
  return (
    <div className="why-now-mobile">
      <header className="why-now-mobile-head">
        <h2>企业 AI 的成本与能力，终于在今天交叉。</h2>
        <p>云端在涨价，本地在变强。成本与能力两条曲线，在此刻交汇。</p>
      </header>

      <article className="now-mobile-beat" data-tone="cost">
        <div className="now-mobile-visual" aria-hidden>
          <img src={plate("cloud")} alt="" />
          <img src={plate("price-up")} alt="" />
          <img src={plate("token-limit")} alt="" />
        </div>
        <NowCopy
          tone="cost"
          title="使用越深入，云端账单越接近基础设施成本"
          body="更多用户、更长流程、更高调用频率，都会转化为持续增长的算力与订阅支出。"
        />
      </article>

      <div className="now-mobile-cross" aria-hidden><span>今天</span></div>

      <article className="now-mobile-beat" data-tone="capability">
        <div className="now-mobile-visual now-mobile-models" aria-hidden>
          <img src={plate("dense-model")} alt="" />
          <img src={plate("deepseek-flash")} alt="" />
          <img src={plate("workstation")} alt="" />
        </div>
        <NowCopy
          tone="capability"
          title="27B 级模型，正在把旗舰能力带回本地"
          body="高效架构让 27B 级模型逼近上代旗舰能力，足以应付绝大多数办公场景，并在消费级硬件上稳定运行。"
        />
      </article>

      <p className="why-now-mobile-close">本地 AI 不再是妥协方案，而是企业智能化的新默认。</p>
    </div>
  );
}

export function WhyNow() {
  return (
    <section className="why-now" id="why-now">
      <NowScene />
      <WhyNowMobile />
    </section>
  );
}
